import { ChangeDetectionStrategy, Component, OnChanges, SimpleChanges, input, inject, model, effect, signal } from '@angular/core';
import { LoggerService } from '@/core/services/logger.service';

import { FormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { CardContent } from '@/shared/components/card-content/card-content';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { finalize } from 'rxjs/operators';
import { ConsultorioPrivadoResponse } from '@/core/api/sicom-api/registro/medico/consultorio-privado/consultorio-privado.model';
import { ConsultorioDadosGeraisFeatureService } from './consultorio-dados-gerais.service';
import { BotaoAcoesType } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { ActionChildComponent } from '../../../../../../../shared/components/containers/action-child.component';
import { MenuItem, MessageService } from 'primeng/api';
import { SwitchSimNao } from '@/shared/components/switch-sim-nao/switch-sim-nao';

/** Valores editáveis da aba de dados gerais, usados para snapshot/restauração no cancelar. */
interface ConsultorioDadosEditaveis {
    situacao: string | null;
    nuRegistroSanitario: string;
    tipoLicenca: string | null;
    dtValidadeRegSanitario: string;
    nuAlvara: string;
    isento: boolean;
    dtValidadeAlvara: string;
    uso: string | null;
    especialidade: boolean;
}

@Component({
    selector: 'app-consultorio-dados-gerias',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SwitchSimNao, FormsModule, InputText, Select, CardContent],
    templateUrl: './consultorio-dados-gerias.html',
    styleUrls: ['./consultorio-dados-gerias.scss']
})
/** Aba de dados gerais do consultório privado. */
export class ConsultorioDadosGeriasComponent extends ActionChildComponent implements OnChanges {
    private readonly logger = inject(LoggerService);
    private readonly feature: ConsultorioDadosGeraisFeatureService = inject(ConsultorioDadosGeraisFeatureService);
    private readonly messageService = inject(MessageService);

    /** Controla se os campos da aba estão liberados para edição (dirigido pelo tab-container). */
    readonly modoEdicao = signal(false);
    /** Estado dos campos ao entrar em edição, para restaurar no cancelar. */
    private snapshotEdicao: ConsultorioDadosEditaveis | null = null;

    // Pessoa de contexto (padrão do componente Anotações)
    pessoa = input<Pessoa>();
    // Consultório selecionado no container
    idConsultorio = input<number | null>(null);

    loading = signal(false);
    erro = signal<string | undefined>(undefined);

    // Select options
    usoOpts = [
        { label: 'Exclusivo', value: 'E' },
        { label: 'Compartilhado', value: 'C' }
    ];

    situacaoOpts = [
        { label: 'Regular', value: 'R' },
        { label: 'Pendente', value: 'P' },
        { label: 'Cancelado', value: 'C' }
    ];

    tipoLicencaOpts = [
        { label: 'Municipal', value: 'M' },
        { label: 'Estadual', value: 'E' }
    ];

    // Model values (preenchidos pela API)
    controle: string = '';
    dtRegistro: string = '';
    nuCnes: string = '';
    situacao: string | null = null;

    nuRegistroSanitario: string = '';
    tipoLicenca: string | null = null;
    dtValidadeRegSanitario: string = '';

    nuAlvara: string = '';
    isento = false; // inIsencaoAlvara ('S'/'N')
    dtValidadeAlvara: string = '';

    uso: string | null = null;
    dtRenovacao: string = '';
    valCertificado = signal('');
    especialidade = false; // inEspecializado ('S'/'N')

    actions: BotaoAcoesType & { useEdit: boolean } = { items: [], useEdit: true };
    save = model<boolean>(false);
    cancel = model<boolean>(false);
    enable = model<boolean>(false);

    constructor() {
        super();
        effect(() => {
            if (this.save()) this.saveForm();
            if (this.cancel()) this.cancelForm();
            if (this.enable()) this.enableForm();
        });
    }

    initComponent(): void {}

    override getDefinedActions(): BotaoAcoesType & { useEdit: boolean } {
        return this.actions;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pessoa'] || changes['idConsultorio']) {
            const p = this.pessoa();
            const idC = this.idConsultorio();
            if (p && p.id !== undefined && p.id !== null && !!p.uf && typeof idC === 'number') {
                this.carregar({ id: p.id, uf: p.uf }, idC);
            } else {
                this.limpar();
            }
        }
    }

    private carregar(params: Pessoa, idConsultorio: number): void {
        this.loading.set(true);
        this.erro.set(undefined);
        this.feature
            .buscar({ ...params, idConsultorio })
            .pipe(finalize(() => this.loading.set(false)))
            .subscribe({
                next: (resp: ConsultorioPrivadoResponse[]) => {
                    const registro = this.selecionarRegistro(resp, idConsultorio);
                    if (registro) {
                        this.preencher(registro);
                    } else {
                        this.limpar();
                    }
                },
                error: (err) => {
                    this.logger.error('Erro ao buscar consultório privado', err);
                    this.erro.set('Falha ao carregar dados do consultório privado.');
                    this.limpar();
                }
            });
        this.carregarValidadeCertificado(params, idConsultorio);
    }

    private preencher(c: ConsultorioPrivadoResponse): void {
        this.controle = c.controle ?? '';
        this.dtRegistro = this.formatarData(c.dtRegistro);
        this.nuCnes = c.nuCnes ?? '';
        this.situacao = this.normalizarSituacao(c.inSituacao);

        this.nuRegistroSanitario = c.nuRegistroSanitario ?? '';
        this.tipoLicenca = this.normalizarTipoLicenca(c.inTipoRegSanitario);
        this.dtValidadeRegSanitario = this.formatarData(c.dtValidadeRegSanitario);

        this.nuAlvara = c.nuAlvara ?? '';
        this.isento = (c.inIsencaoAlvara ?? '').toUpperCase() === 'S';
        this.dtValidadeAlvara = this.formatarData(c.dtValidadeAlvara);

        this.uso = this.normalizarUso(c.inUso);
        this.dtRenovacao = this.formatarData(c.dtAtualizacao);
        this.especialidade = (c.inEspecializado ?? '').toUpperCase() === 'S';
    }

    private limpar(): void {
        this.controle = '';
        this.dtRegistro = '';
        this.nuCnes = '';
        this.situacao = null;

        this.nuRegistroSanitario = '';
        this.tipoLicenca = null;
        this.dtValidadeRegSanitario = '';

        this.nuAlvara = '';
        this.isento = false;
        this.dtValidadeAlvara = '';

        this.uso = null;
        this.dtRenovacao = '';
        this.valCertificado.set('');
        this.especialidade = false;
    }

    private formatarData(valor?: string | null): string {
        if (!valor) return '';
        const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) {
            const [, yyyy, mm, dd] = m;
            return `${dd}/${mm}/${yyyy}`;
        }
        try {
            const d = new Date(valor);
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            if (!isNaN(d.getTime())) return `${dd}/${mm}/${yyyy}`;
            return '';
        } catch {
            return '';
        }
    }

    private carregarValidadeCertificado(params: Pessoa, idConsultorio: number): void {
        this.valCertificado.set('');
        this.feature.buscarCertificadosMedico({ ...params, idConsultorio }).subscribe({
            next: (lista) => {
                const certificados = Array.isArray(lista) ? lista : [];
                const certificado = certificados.find((item) => Number(item.idConsultorio) === Number(idConsultorio)) ?? certificados[0] ?? null;
                this.valCertificado.set(this.formatarData(certificado?.dtValidade));
            },
            error: () => {
                this.valCertificado.set('');
            }
        });
    }

    private selecionarRegistro(lista: ConsultorioPrivadoResponse[] | null | undefined, idConsultorio: number): ConsultorioPrivadoResponse | null {
        if (!Array.isArray(lista) || lista.length === 0) return null;
        return lista.find((item) => item.idConsultorio === idConsultorio) ?? lista[0];
    }

    private normalizarUso(valor?: string | null): string | null {
        const raw = (valor ?? '').toString().trim().toUpperCase();
        if (!raw) return null;
        if (raw === 'EXCLUSIVO' || raw === 'E') return 'E';
        if (raw === 'COMPARTILHADO' || raw === 'C') return 'C';
        return null;
    }

    private normalizarSituacao(valor?: string | null): string | null {
        const raw = (valor ?? '').toString().trim().toUpperCase();
        if (!raw) return null;
        if (raw === 'REGULAR' || raw === 'R') return 'R';
        if (raw === 'PENDENTE' || raw === 'P') return 'P';
        if (raw === 'CANCELADO' || raw === 'C') return 'C';
        return null;
    }

    private normalizarTipoLicenca(valor?: string | null): string | null {
        const raw = (valor ?? '').toString().trim().toUpperCase();
        if (!raw) return null;
        if (raw === 'MUNICIPAL' || raw === 'M') return 'M';
        if (raw === 'ESTADUAL' || raw === 'E') return 'E';
        return null;
    }

    private saveForm() {
        // TODO(persistência): o sicom-api ainda não expõe endpoint de atualização dos dados gerais
        // do consultório privado (não há PUT/PATCH em MedicoConsultorioController; o
        // ConsultorioPrivadoService só tem listar/buscar/inserir). Enquanto o contrato não existir,
        // a edição NÃO é persistida — não simular sucesso de gravação.
        this.logger.info('Edição de dados gerais do consultório não é persistida: endpoint de atualização pendente.');
        this.messageService.add({
            severity: 'info',
            summary: 'Edição não gravada',
            detail: 'A gravação depende de um endpoint de atualização ainda não disponível no servidor.'
        });
        this.snapshotEdicao = null;
        this.modoEdicao.set(false);
        this.save.set(false);
        this.enable.set(false);
    }

    private cancelForm() {
        if (this.snapshotEdicao) this.restaurarValoresEditaveis(this.snapshotEdicao);
        this.snapshotEdicao = null;
        this.modoEdicao.set(false);
        this.cancel.set(false);
        this.enable.set(false);
    }

    private enableForm() {
        this.snapshotEdicao = this.capturarValoresEditaveis();
        this.modoEdicao.set(true);
    }

    /** Fotografa os campos editáveis no início da edição. */
    private capturarValoresEditaveis(): ConsultorioDadosEditaveis {
        return {
            situacao: this.situacao,
            nuRegistroSanitario: this.nuRegistroSanitario,
            tipoLicenca: this.tipoLicenca,
            dtValidadeRegSanitario: this.dtValidadeRegSanitario,
            nuAlvara: this.nuAlvara,
            isento: this.isento,
            dtValidadeAlvara: this.dtValidadeAlvara,
            uso: this.uso,
            especialidade: this.especialidade
        };
    }

    /** Restaura os campos editáveis a partir do snapshot (usado ao cancelar). */
    private restaurarValoresEditaveis(valores: ConsultorioDadosEditaveis): void {
        this.situacao = valores.situacao;
        this.nuRegistroSanitario = valores.nuRegistroSanitario;
        this.tipoLicenca = valores.tipoLicenca;
        this.dtValidadeRegSanitario = valores.dtValidadeRegSanitario;
        this.nuAlvara = valores.nuAlvara;
        this.isento = valores.isento;
        this.dtValidadeAlvara = valores.dtValidadeAlvara;
        this.uso = valores.uso;
        this.especialidade = valores.especialidade;
    }
    override updateActionsWithIndexes(_actions: [MenuItem, number][]) {}
    override toggleAction(action: MenuItem): MenuItem {
        return action;
    }
}
